import { useEffect, useLayoutEffect, useRef, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import { Button, Icon } from "../../components/design";
import styles from "./PermisssionWidget.module.css";

// The widget is on Permisso's site, so we cannot measure it. If it tells us
// its height in a message, the frame takes that height (no inner scroll).
// ponytail: Permisso's message format is not documented; these are the usual
// shapes. Check the dev console log below and keep only the real one.
const readHeight = (data) => {
  const value = Number(
    data?.height ?? data?.data?.height ?? data?.payload?.height
  );
  return Number.isFinite(value) && value > 0 ? value : null;
};

// keeps a broken or looping message from making a giant or tiny frame
const MIN_HEIGHT = 320;
const MAX_HEIGHT = 4000;

// Without a height message the frame has a fixed height (59.25rem, the top
// 2rem hidden under the card edge). On short desktop screens that does not
// fit, so we draw the frame at its normal width and a shorter height that
// still holds Permisso's first screen (FIT_FRAME_REM), then scale the frame
// and the card down together so the card hugs the widget.
// Below MIN_SCALE the text gets too small; the overlay scrolls instead.
const FRAME_REM = 59.25;
const FIT_FRAME_REM = 50;
const CARD_REM = 43.75;
const CROP_REM = 2;
const MIN_SCALE = 0.6;
const DESKTOP_QUERY = "(min-width: 769px)";

const useFitScale = (cardRef, enabled) => {
  const [fit, setFit] = useState(null);

  useLayoutEffect(() => {
    if (!enabled) {
      setFit(null);
      return undefined;
    }
    let request = 0;
    const measure = () => {
      request = 0;
      const card = cardRef.current;
      if (!card || !window.matchMedia(DESKTOP_QUERY).matches) {
        setFit(null);
        return;
      }
      const rem = parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
      const cardStyle = getComputedStyle(card);
      const available =
        window.innerHeight -
        parseFloat(cardStyle.marginTop) -
        parseFloat(cardStyle.marginBottom);
      const crop = CROP_REM * rem;
      if (available >= FRAME_REM * rem - crop) {
        setFit(null);
        return;
      }
      const frame = FIT_FRAME_REM * rem;
      const scale = Math.min(
        Math.max(available / (frame - crop), MIN_SCALE),
        1
      );
      const parent = card.parentElement;
      const parentStyle = getComputedStyle(parent);
      const width = Math.min(
        CARD_REM * rem,
        parent.clientWidth -
          parseFloat(parentStyle.paddingLeft) -
          parseFloat(parentStyle.paddingRight)
      );
      setFit({ scale, frame, crop, width });
    };
    const schedule = () => {
      if (!request) request = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(request);
      window.removeEventListener("resize", schedule);
    };
  }, [cardRef, enabled]);

  return fit;
};

// inline: shown as the onboarding page itself, under a Back button. Without
// a height message the frame fills the page height, so only Permisso's own
// page scrolls, never the page around it.
const PermissoWidget = ({
  widgetUrl,
  onClose,
  onSuccess,
  onError,
  inline = false,
}) => {
  const { _t } = useTranslator();
  const [frameHeight, setFrameHeight] = useState(null);
  const pendingHeight = useRef(null);
  const frameRequest = useRef(0);
  const cardRef = useRef(null);
  const fit = useFitScale(cardRef, !inline && frameHeight === null);

  useEffect(() => () => cancelAnimationFrame(frameRequest.current), []);

  useEffect(() => {
    const handleMessage = (event) => {
      // Allowing all origins on dev only
      const allowedOrigin = [
        "https://widget.bankflip.io",
        "https://widget.permisso.io",
        "https://widget.sandbox.bankflip.io",
        "https://widget.sandbox.permisso.io",
      ];

      if (!allowedOrigin.includes(event?.origin)) {
        return;
      }

      const data = event?.data;

      if (process.env.NODE_ENV === "development") {
        console.debug("[Permisso widget message]", data);
      }

      const height = readHeight(data);
      if (height) {
        // at most one update per frame, and only for a real change
        pendingHeight.current = Math.min(
          Math.max(Math.ceil(height), MIN_HEIGHT),
          MAX_HEIGHT
        );
        if (!frameRequest.current) {
          frameRequest.current = requestAnimationFrame(() => {
            frameRequest.current = 0;
            const next = pendingHeight.current;
            setFrameHeight((current) =>
              current !== null && Math.abs(current - next) < 4 ? current : next
            );
          });
        }
        return;
      }

      if (data?.name === "user_requested_closure") {
        onSuccess && onSuccess(data);
        onClose && onClose();
      } else if (
        ["session_cancelled", "token_expired", "session_expired"].includes(
          data?.name
        )
      ) {
        onError && onError(data);
        onClose && onClose();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [onClose, onSuccess, onError]);

  if (inline) {
    return (
      <div
        className={`${styles.inlineWidget} ${frameHeight ? "" : styles.fill}`}
      >
        <Button
          variant="ghost"
          className={styles.backButton}
          iconStart={<Icon name="chevronLeft" size={16} />}
          onClick={() => onClose && onClose()}
        >
          {_t("back")}
        </Button>
        <iframe
          src={widgetUrl}
          className={styles.inlineFrame}
          style={frameHeight ? { height: frameHeight } : undefined}
          scrolling={frameHeight ? "no" : undefined}
          title="Permisso Widget"
          allow="camera; microphone; clipboard-read; clipboard-write"
        />
      </div>
    );
  }

  return (
    <div
      className={`popup-overlay wrapper-modal modal-fixed-screen ${styles.widgetOverlay}`}
    >
      <div
        ref={cardRef}
        className={styles.widgetCard}
        style={
          fit
            ? {
                width: fit.width * fit.scale,
                height: (fit.frame - fit.crop) * fit.scale,
              }
            : undefined
        }
      >
        <iframe
          src={widgetUrl}
          className={styles.widgetFrame}
          style={
            frameHeight
              ? { height: frameHeight }
              : fit
                ? {
                    width: fit.width,
                    height: fit.frame,
                    marginTop: -fit.crop * fit.scale,
                    transform: `scale(${fit.scale})`,
                    transformOrigin: "top left",
                  }
                : undefined
          }
          scrolling={frameHeight ? "no" : undefined}
          title="Permisso Widget"
          allow="camera; microphone; clipboard-read; clipboard-write"
        />
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          className={styles.closeButton}
          iconStart={<Icon name="close" size={18} />}
          onClick={() => onClose && onClose()}
          aria-label="Close"
        />
      </div>
    </div>
  );
};

export default PermissoWidget;
